import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocket, useSocketEvent } from '../context/SocketContext';
import StatusSelect from '../components/StatusSelect';
import { StatusBadge } from '../components/Badges';
import ConfirmDialog from '../components/ConfirmDialog';
import PatientDetailModal from '../components/PatientDetailModal';
import NowServingDisplay from '../components/NowServingDisplay';
import { RefreshCw, RotateCcw, Search, Inbox, Wifi, WifiOff, Clock } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';
import QRDisplay from '../components/QRDisplay';

// ── Rescheduled detail badge — click to show/hide details ────────────────────
function RescheduledBadge({ rescheduledFrom, rescheduledDate, rescheduledTo, rescheduledAt, newSlot, newDate }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const [pos, setPos]   = React.useState({ top:0, left:0 });
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      let left = r.left;
      if (left + 280 > window.innerWidth - 10) left = window.innerWidth - 290;
      setPos({ top: r.bottom + 8, left: Math.max(left, 8) });
    }
    setOpen(o => !o);
  }

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
          background: 'var(--color-warning-soft)',
          color: 'var(--color-warning)',
          border: '1px solid var(--color-warning)',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
        ⟳ Rescheduled
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:1099 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 1100,
            width: 280,
            background: 'var(--color-surface)',
            border: '2px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
          }}>
            {/* Header bar */}
            <div style={{ background: 'var(--color-warning)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>⟳ Reschedule Details</span>
            </div>

            <div style={{ padding: 14, display: 'grid', gap: 10 }}>
              {/* Original — red tint */}
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--color-danger-soft)',
                border: '1px solid var(--color-danger)',
              }}>
                <div style={{ fontSize: 10, color: 'var(--color-danger)', fontWeight: 800, marginBottom: 4, letterSpacing: '0.06em' }}>ORIGINAL (CANCELLED)</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-ink)' }}>{rescheduledFrom || '—'}</div>
                <div style={{ color: 'var(--color-ink-soft)', fontSize: 11, marginTop: 2 }}>Date: {rescheduledDate || '—'}</div>
              </div>

              {/* New slot — green tint */}
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--color-success-soft)',
                border: '1px solid var(--color-success)',
              }}>
                <div style={{ fontSize: 10, color: 'var(--color-success)', fontWeight: 800, marginBottom: 4, letterSpacing: '0.06em' }}>NEW SLOT</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-primary)' }}>{newSlot || rescheduledTo || '—'}</div>
                <div style={{ color: 'var(--color-ink-soft)', fontSize: 11, marginTop: 2 }}>Date: {newDate || '—'}</div>
              </div>

              {rescheduledAt && (
                <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', paddingTop: 2 }}>
                  Rescheduled at: {fmt(rescheduledAt)}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export default function QueuePage() {
  const [patients, setPatients] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('queue_viewMode') || 'list');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const { showToast } = useToast();
  const { user } = useAuth();
  const { connected } = useSocket();

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get('/patients/queue');
      setPatients(res.data.patients);
    } catch (err) {
      showToast('Could not load the queue.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadQueue();
    // Safety-net poll in case the real-time socket connection ever drops
    // silently (e.g. a flaky network) - real-time events below handle the
    // normal case instantly, this is just a backup so the screen never
    // goes stale for more than a minute.
    const interval = setInterval(loadQueue, 60000);
    return () => clearInterval(interval);
  }, [loadQueue]);

  // Real-time: any other tab/device (doctor or receptionist) creating a
  // token, changing a status, or archiving a patient pushes an instant
  // update here - no manual refresh needed.
  useSocketEvent('queue:updated', () => loadQueue());

  // When someone (possibly in another tab) resets the daily queue, reload
  // immediately and let this user know what just happened, since their
  // screen is about to visibly empty out.
  useSocketEvent('queue:reset', (payload) => {
    showToast(
      `The daily queue was just reset (${payload?.modifiedCount ?? 0} token${payload?.modifiedCount === 1 ? '' : 's'} archived). Past patient records are still available under "All Patients".`,
      'info'
    );
    loadQueue();
  });

  function handlePatientUpdated(updated) {
    setPatients((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
  }

  async function handleResetDaily() {
    setResetting(true);
    try {
      const res = await api.post('/patients/reset-daily');
      showToast(res.data.message, 'success');
      setResetConfirmOpen(false);
      loadQueue();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not reset the queue.', 'error');
    } finally {
      setResetting(false);
    }
  }

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.patientUID.toLowerCase().includes(q) ||
      (p.phone || '').includes(q)
    );
  });

  return (
    <AppLayout title="Today's Queue" subtitle={`${patients.length} patient${patients.length !== 1 ? 's' : ''} in today's queue`}>
      <div style={{ marginBottom: 20 }}>
        <NowServingDisplay showNextButton onAdvanced={() => loadQueue()} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Search by name, ID, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div
          title={connected ? 'Live updates connected' : 'Live updates disconnected — reconnecting...'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: connected ? 'var(--color-success)' : 'var(--color-warning)',
            padding: '0 4px',
          }}
        >
          {connected ? <Wifi size={15} /> : <WifiOff size={15} />}
          {connected ? 'Live' : 'Reconnecting'}
        </div>
        <QRDisplay />
        {/* View mode switcher */}
        <div style={{ display:'flex', border:'1px solid var(--color-border)', borderRadius:8, overflow:'hidden' }}>
          {[
            { key:'list',    icon:'☰',  title:'List view' },
            { key:'compact', icon:'⋮⋮', title:'Compact view' },
            { key:'card',    icon:'⊞',  title:'Card grid view' },
          ].map(v => (
            <button key={v.key} type="button" title={v.title}
              onClick={() => { setViewMode(v.key); localStorage.setItem('queue_viewMode', v.key); }}
              className={`btn btn-sm ${viewMode===v.key?'btn-primary':'btn-ghost'}`}
              style={{ borderRadius:0, padding:'6px 10px', border:'none' }}>
              {v.icon}
            </button>
          ))}
        </div>
        <button className="btn btn-outline" onClick={loadQueue}>
          <RefreshCw size={15} />
          Refresh
        </button>
        <button className="btn btn-outline" onClick={() => setResetConfirmOpen(true)}>
          <RotateCcw size={15} />
          Reset Daily Tokens
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 88, borderRadius: 10 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Inbox size={36} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>No patients in today's queue yet.</p>
          <p style={{ margin: '4px 0 0', fontSize: 13 }}>
            {user?.role === 'receptionist' ? 'Create a new token to get started.' : 'Waiting for the receptionist to add patients.'}
          </p>
        </div>
      ) : viewMode === 'compact' ? (
        /* ── Compact view ─────────────────────────────────────────────── */
        <div className="card" style={{ overflow:'hidden' }}>
          {filtered.map((p, i) => (
            <div key={p._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 14px', borderTop: i===0?'none':'1px solid var(--color-border)', gap:10, flexWrap:'wrap', background: i%2===0?'transparent':'var(--color-surface)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
                <div style={{ width:36, height:36, borderRadius:8, background:'var(--color-primary)', color:'white', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:9, opacity:0.8, lineHeight:1 }}>T</span>
                  <span style={{ fontSize:16, fontWeight:800, lineHeight:1 }}>{p.tokenNumber}</span>
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>{p.name}</div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)', display:'flex', gap:8, flexWrap:'wrap' }}>
                    {p.phone && <span>{p.phone}</span>}
                    {p.appointmentSlot && <span style={{ color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}</span>}
                    {p.isRescheduled && <span style={{ color:'#d97706', fontWeight:700 }}>⟳ Rescheduled</span>}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                <StatusBadge status={p.status} />
                {p.phone && <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" />}
                <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'card' ? (
        /* ── Card grid view ───────────────────────────────────────────── */
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
          {filtered.map((p) => (
            <div key={p._id} className="card" style={{ padding:14, display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'var(--color-primary)', color:'white', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:8, opacity:0.8 }}>TOKEN</span>
                  <span style={{ fontSize:18, fontWeight:800, lineHeight:1 }}>{p.tokenNumber}</span>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div style={{ fontWeight:700, fontSize:14, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>{p.name}</div>
              <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>
                {p.gender}{p.age?`, ${p.age}y`:''} {p.phone && `· ${p.phone}`}
              </div>
              {p.appointmentSlot && <div style={{ fontSize:11, fontWeight:600, color:'var(--color-primary)' }}>⏰ {p.appointmentSlot}</div>}
              {p.isRescheduled && <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:100, background:'#fef3c7', color:'#d97706', border:'1px solid #fcd34d', width:'fit-content' }}>⟳ Rescheduled</span>}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:'auto' }}>
                <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
                {p.phone && <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" />}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Default list view ────────────────────────────────────────── */
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map((p) => (
            <div key={p._id} className="ticket">
              <div className="ticket-stub">
                <div className="ticket-stub-label">Token</div>
                <div className="ticket-stub-number">{p.tokenNumber}</div>
              </div>
              <div className="ticket-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div className="ticket-name" style={{ cursor: 'pointer' }} onClick={() => setSelectedPatient(p)}>
                      {p.name}
                    </div>
                    <div className="ticket-meta">
                      <span>{p.gender}{p.age ? `, ${p.age}y` : ''}</span>
                      {p.phone && <span>· {p.phone}</span>}
                      <span className="ticket-uid">· {p.patientUID}</span>
                    </div>
                    {p.reasonForVisit && (
                      <div style={{ fontSize: 13, color: 'var(--color-ink-soft)', marginBottom: 4 }}>{p.reasonForVisit}</div>
                    )}
                    {p.appointmentSlot && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <Clock size={12} />
                        {p.appointmentSlot}
                        {p.appointmentDate && p.appointmentDate !== p.tokenDate && (
                          <span style={{ fontWeight: 400, color: 'var(--color-ink-faint)' }}>· {p.appointmentDate}</span>
                        )}
                      </div>
                    )}
                    {p.isRescheduled && (
                      <RescheduledBadge rescheduledFrom={p.rescheduledFrom} rescheduledDate={p.rescheduledDate} rescheduledTo={p.rescheduledTo} rescheduledAt={p.rescheduledAt} newSlot={p.appointmentSlot} newDate={p.appointmentDate} />
                    )}
                  </div>
                </div>
                <div className="ticket-actions">
                  <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelectedPatient(p)}>
                    View details
                  </button>
                  {p.phone && (
                    <WhatsAppButton
                      patient={p}
                      slot={p.appointmentSlot}
                      date={p.tokenDate}
                      tokenNumber={p.tokenNumber}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={resetConfirmOpen}
        title="Reset daily tokens?"
        message="This will clear today's active queue so you can start fresh tomorrow. All patient records and history stay safely stored in the database — nothing is permanently deleted."
        confirmLabel={resetting ? 'Resetting...' : 'Yes, reset queue'}
        onConfirm={handleResetDaily}
        onCancel={() => setResetConfirmOpen(false)}
      />

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          editable={user?.role === 'doctor'}
          onClose={() => setSelectedPatient(null)}
          onUpdated={(updated) => {
            handlePatientUpdated(updated);
            setSelectedPatient(updated);
          }}
        />
      )}
    </AppLayout>
  );
}
